import axios, { AxiosError } from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, apiRequest } from '@/auth/msalConfig';
import { isAuthBypassEnabled } from '@/auth/authSettings';

const baseURL = import.meta.env.VITE_API_BASE_URL || '';

export const apiClient = axios.create({
  baseURL,
});

// Attach a bearer token to every request.
apiClient.interceptors.request.use(async (config) => {
  if (isAuthBypassEnabled) {
    return config;
  }

  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (account) {
    try {
      const result = await msalInstance.acquireTokenSilent({
        ...apiRequest,
        account,
      });
      config.headers.Authorization = `Bearer ${result.accessToken}`;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await msalInstance.acquireTokenRedirect(apiRequest);
      }
      throw error;
    }
  }
  return config;
});

/** Shape of an ASP.NET Core `ProblemDetails` payload returned on API error responses. */
export interface ApiProblem {
  title?: string;
  detail?: string;
  status?: number;
  errors?: Record<string, string[]>;
}

/**
 * Extracts a human-readable message from an API error response.
 *
 * Prefers, in order: field validation messages, a `detail` string, a `title`
 * string, a raw string response body, an HTTP status fallback, and finally
 * the underlying Axios error message.
 *
 * @param error - The error thrown by an Axios request, typically an `AxiosError<ApiProblem>`.
 * @returns A trimmed, user-displayable error message.
 */
export function getApiErrorMessage(error: unknown): string {
  const axiosError = error as AxiosError<ApiProblem>;
  const data = axiosError.response?.data;

  if (data?.errors) {
    const validationMessage = Object.values(data.errors)
      .flat()
      .map((x) => x?.trim())
      .filter(Boolean)
      .join(' ');

    if (validationMessage) {
      return validationMessage;
    }
  }

  const detail = typeof data?.detail === 'string' ? data.detail.trim() : '';
  if (detail) {
    return detail;
  }

  const title = typeof data?.title === 'string' ? data.title.trim() : '';
  if (title) {
    return title;
  }

  const rawData = axiosError.response?.data as unknown;
  if (typeof rawData === 'string' && rawData.trim()) {
    return rawData.trim();
  }

  if (axiosError.response?.status) {
    return `Request failed (${axiosError.response.status}).`;
  }

  return axiosError.message?.trim() || 'An unexpected error occurred.';
}
